import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, MenuItem, Paper, Select, TextField, Typography, IconButton, useTheme, FormHelperText } from '@mui/material'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { useSettingsStore } from '../../../store/settings.store'
import api from '../../../service/api'
import { Icons } from '../../../utils/utilities'
import CustomSelectField from '../../common/CustomSelectField'
import CustomChip from '../../common/CustomChip'


const settingValueDataTypes = [
    { label: "Boolean", value: "boolean" },
    { label: "Multiple", value: "multiple" },
    { label: "Number", value: "number" },
    { label: "Select(Pre-defined options)", value: "select" },
    { label: "Text", value: "text" },
    { label: "File", value: "file" },
]

const AddSetting = ( { defaultModule, dialogState, setState, getSelectedSettings, setAddModuleDialogState, parents } ) => {

    const [adding, setAdding] = useState( false )

    const SettingsStore = useSettingsStore()

    const { palette, border } = useTheme()

    const initialValues = {
        setting_label: '',
        setting_description: '',
        setting_data_type: 'text',
        module: defaultModule || SettingsStore.getModules[0]?.module,
        setting_key: '',
        parent: 'none',
        setting_unit: '',
        default_value: '',
        setting_options: []
    }

    const validationSchema = Yup.object().shape( {
        setting_label: Yup.string().required( "Setting label is required!" ),
        setting_key: Yup.string().required( "Setting key is required!" ),
        default_value: Yup.string().test( "DEFAULT VALUE CEHCK", "The default value is required", ( val, { parent } ) => {
            if ( parent.setting_data_type === 'file' ) return true
            else if ( !val || val.trim() === "" ) return false
            return true
        } )
    } )

    const handleParentChange = ( e, setFieldValue ) => {
        if ( e.target.value === "none" ) {
            setFieldValue( 'module', 'general' )
            setFieldValue( 'parent', 'none' )
        } else {
            const selectedParent = parents.find( p => p.id === e.target.value )
            setFieldValue( 'module', selectedParent.module )
            setFieldValue( 'parent', e.target.value )
        }
    }

    const handleKeyDownForOptionsInput = ( e, setFieldValue, options ) => {
        if ( e.key === "Enter" )
            if ( /^[a-zA-Z0-9\s]+$/.test( e.target.value ) && !options.includes( e.target.value ) ) {
                e.preventDefault()
                console.log( "Options", options )
                setFieldValue( 'setting_options', [...options, e.target.value] )
                e.target.value = ''
                e.target.focus()
            } else {
                e.preventDefault()
                toast.error( "Options should be unique and should only contain numbers and alphpabets" )
            }
    }

    const handleKeyValueChange = ( e, setValue ) => {
        const val = e.target.value
        if ( val && val.trim() !== "" )
            setValue( 'setting_key', val.replace( /[^0-9a-zA-Z]/g, "_" ).toLowerCase() )
        else
            setValue( 'setting_key', "" )
    }

    const deleteOption = ( options, setFieldValue, index ) => {
        let newOptions = [...options]
        newOptions.splice( index, 1 )
        setFieldValue( 'setting_options', newOptions )
    }

    const addSetting = async ( values, { resetForm } ) => {
        setAdding( true )
        try {
            await api.addSetting( values )
            await getSelectedSettings( 'instituteandmodule', true )
            toast.success( `New setting added successfully` )
            resetForm()
            setState( false )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong!" )
        } finally {
            setAdding( false )
        }
    }

    const openAddModuleDialog = () => {
        setState( false )
        setAddModuleDialogState( true )
    }

    return (
        <Dialog open={dialogState} slotProps={{
            paper: { sx: { width: "90vw", maxWidth: "800px" } }
        }}>
            <Box padding="10px 20px" sx={{ borderBottom: border[1], display: 'flex', alignItems: "center", justifyContent: 'space-between' }} >
                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Add new setting</Typography>
                <IconButton onClick={() => setState( false )} size="small">
                    {Icons.default.CloseIcon}
                </IconButton>
            </Box>
            <DialogContent className='custom-scrollbar'>
                <Formik onSubmit={addSetting} initialValues={initialValues} validationSchema={validationSchema}>
                    {( { values, setFieldValue } ) => (
                        <Form>
                            <FormControl fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-label" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting label * </Typography>
                                <Field placeholder="Eg: Max volume for videos" size="small" sx={{ fontSize: "14px" }} as={TextField} name='setting_label' id="setting-label" type='text' />
                                <FormHelperText><ErrorMessage name='setting_label' /></FormHelperText>
                            </FormControl>
                            <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-description" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Description * </Typography>
                                <Field placeholder="Eg: The maximum volume value for the video editor, ranges between 0-1" size="small" sx={{ fontSize: "14px" }} as={TextField} name='setting_description' rows={4} multiline id="setting-description" type='text' />
                                <FormHelperText><ErrorMessage name='setting_description' /></FormHelperText>
                            </FormControl>
                            <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-parent" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Parent</Typography>
                                <Field
                                    size="small"
                                    sx={{ fontSize: "14px" }}
                                    name='parent'
                                    value={values.parent}
                                    onChange={( e ) => handleParentChange( e, setFieldValue )}
                                    as={CustomSelectField}
                                    id="setting-parent"
                                    data={[{ setting_label: "No parent", module: "None", id: "none" }, ...parents]}
                                    placeholder="Select setting parent"
                                    itemValueKey="id"
                                    renderValue={value => parents.find( item => item.id === value )?.setting_label || "None"}
                                    renderOptionText={item => (
                                        <Box key={item.id}>
                                            <Typography variant='subtitle2' color={item.id === "none" ? "error" : 'textPrimary'} component="p">{item.setting_label}</Typography>
                                            {item.module && <CustomChip value={item.module} />}
                                        </Box>
                                    )}
                                >
                                    {parents.map( parent => (
                                        <MenuItem sx={{ whiteSpace: "normal" }} dense key={parent.id} value={parent.id}> <Typography noWrap variant='body1' sx={{ fontSize: "14px", whiteSpace: 'normal', maxWidth: "calc(100% - 5px)" }} >[{parent.module}] {parent.setting_label}</Typography> </MenuItem>
                                    ) )}
                                </Field>

                            </FormControl>

                            <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-key" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting key *</Typography>
                                <Field size="small" sx={{ fontSize: "14px" }} as={TextField} placeholder="Unique identifier for the setting" onChange={( e ) => handleKeyValueChange( e, setFieldValue )} name='setting_key' id="setting-key" type='text' />
                                {values.setting_key && <Typography variant='subtitle2'>{values.setting_key}</Typography>}
                                <FormHelperText><ErrorMessage name='setting_key' /></FormHelperText>
                            </FormControl>

                            <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-module" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Module *</Typography>
                                <Field
                                    size="small"
                                    disabled={values.parent !== 'none'}
                                    sx={{ fontSize: "14px" }}
                                    as={CustomSelectField}
                                    name='module'
                                    id="setting-module"
                                    value={values.module}
                                    data={SettingsStore.getModules}
                                    placeholder="Select Module"
                                    itemValueKey="module"
                                    itemIdentityKey="id"
                                    renderOptionText={item => item.label}
                                    optionsMaxHeight="200px"
                                    customStartActions={[<Button onClick={openAddModuleDialog} color='greyed' size='small' fullWidth startIcon={Icons.default.AddCircleIcon}>Add New module</Button>]}
                                />
                            </FormControl>

                            <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-type" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Type *</Typography>
                                <Field size="small"
                                    sx={{ fontSize: "14px" }}
                                    data={settingValueDataTypes}
                                    itemValueKey="value"
                                    renderOptionText={item => item.label}
                                    as={CustomSelectField}
                                    onChange={( e ) => {
                                        if ( e.target.value === 'boolean' )
                                            setFieldValue( 'default_value', false )
                                        else if ( e.target.value === 'select' )
                                            setFieldValue( 'default_value', 'none' )
                                        else {
                                            setFieldValue( 'default_value', '' )
                                            setFieldValue( 'setting_options', [] )
                                        }
                                        setFieldValue( 'setting_data_type', e.target.value )
                                    }}
                                    name='setting_data_type'
                                    id="setting-type"
                                    value={values.setting_data_type}
                                />

                                <Typography variant='subtitle2' placeholder="Unique identifier for the setting" fontSize="12px" color="error"><ErrorMessage name='setting_data_type' /></Typography>
                            </FormControl>

                            {["text", "number", "multiple"].includes( values.setting_data_type ) && <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-default-value" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Default Value *</Typography>
                                <Field placeholder="Eg: 0.5" size="small" sx={{ fontSize: "14px" }} as={TextField} name='default_value' id="setting-default-value" type='text' />
                                {values.setting_data_type === 'multiple' && <Typography variant='subtitle2' color="GrayText">Enter as comma separated values</Typography>}
                                <FormHelperText><ErrorMessage name='default_value' /></FormHelperText>
                            </FormControl>}

                            {values.setting_data_type === 'select' && <Box>
                                <Paper>
                                    <Box padding="10px" display="flex" alignItems="center" flexWrap="wrap" gap="10px">
                                        {values.setting_options.map( ( option, index ) => (
                                            <Chip key={option} onDelete={() => deleteOption( values.setting_options, setFieldValue, index )} size="small" label={option} />
                                        ) )}
                                        <TextField onKeyDown={( e ) => { handleKeyDownForOptionsInput( e, setFieldValue, values.setting_options ) }} type="text" variant='standard' sx={{ width: "300px", minWidth: "200px", border: "none", outline: "none", padding: "5px" }} size="small" placeholder='Type options and press enter to add...' slotProps={{
                                            input: { disableUnderline: true, sx: { border: "none", outline: "none", fontSize: "14px" } }
                                        }} />
                                    </Box>
                                </Paper>
                                <FormControl sx={{ marginBottom: "10px", marginTop: "20px" }} fullWidth>
                                    <Typography component="label" display="block" htmlFor="setting-default-value-for-select" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Default Value *</Typography>
                                    <Field size="small" as={Select} value={values.default_value} name='default_value' id="setting-default-value-for-select">
                                        <MenuItem disabled dense sx={{ fontSize: "14px" }} value={"none"}>None</MenuItem>
                                        {values.setting_options.map( option => (
                                            <MenuItem key={option} sx={{ fontSize: "14px" }} dense value={option}>{option}</MenuItem>
                                        ) )}
                                    </Field>
                                    <FormHelperText><ErrorMessage name='default_value' /></FormHelperText>
                                </FormControl>
                            </Box>}
                            {values.setting_data_type === 'boolean' && <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-default-value-for-bool" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Default Value *</Typography>
                                <Field size="small" sx={{ fontSize: "14px" }} as={Select} name='default_value' id="setting-default-value-for-bool">
                                    <MenuItem sx={{ fontSize: "14px" }} value={true}>True</MenuItem>
                                    <MenuItem sx={{ fontSize: "14px" }} value={false}>False</MenuItem>
                                </Field>
                                <FormHelperText><ErrorMessage name='default_value' /></FormHelperText>
                            </FormControl>}
                            {!['boolean', 'select', 'multiple', 'file'].includes( values.setting_data_type ) && <FormControl margin="normal" fullWidth>
                                <Typography component="label" display="block" htmlFor="setting-unit" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Setting Unit</Typography>
                                <Field placeholder="Eg: MB" size="small" sx={{ fontSize: "14px" }} as={TextField} name='setting_unit' id="setting-unit" type='text' />
                                <FormHelperText><ErrorMessage name='setting_unit' /></FormHelperText>
                            </FormControl>}

                            <Box marginTop="40px" display="flex" gap="10px" alignItems="center">
                                <Button startIcon={adding ? <CircularProgress size={14} /> : Icons.default.AddIcon} disabled={adding} type="submit" disableElevation sx={{ textTransform: "capitalize", display: "flex", alignItems: "center", gap: "10px" }} variant='contained'> {adding ? "Adding setting..." : "Add setting"}</Button>
                                <Button onClick={() => setState( false )} color="error" sx={{ textTransform: "capitalize" }} variant="outlined">Cancel</Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    )
}

export default AddSetting